/**
 * The single date policy of PRD §6.6, shared by client and server.
 *
 * Business dates are `YYYY-MM-DD` strings end to end; "today" is always Europe/Berlin and is
 * decided server-side. Nothing else in this codebase may parse a business date or ask the
 * host clock what day it is.
 */

/** A business date in `YYYY-MM-DD` form, as stored in Postgres `date` columns. */
export type BusinessDate = string;

const BUSINESS_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

// en-CA formats as YYYY-MM-DD, which is the wire format we store.
const berlinFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Berlin",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Today in Europe/Berlin. The DB counterpart is `berlin_today()`; the two must agree. */
export function berlinToday(now: Date = new Date()): BusinessDate {
  return berlinFormatter.format(now);
}

const berlinHourFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Berlin",
  hour: "numeric",
  hour12: false,
  hourCycle: "h23",
});

/** Hour of day in Europe/Berlin (0–23). Matches SQL `(timestamptz at time zone 'Europe/Berlin')::time` hour. */
export function berlinHour(now: Date = new Date()): number {
  return Number(berlinHourFormatter.format(now));
}

function partsOf(value: BusinessDate): { year: number; month: number; day: number } {
  return {
    year: Number(value.slice(0, 4)),
    month: Number(value.slice(5, 7)),
    day: Number(value.slice(8, 10)),
  };
}

export function isBusinessDate(value: string): boolean {
  if (!BUSINESS_DATE_PATTERN.test(value)) return false;
  const { year, month, day } = partsOf(value);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/**
 * The one parse helper (PRD §6.6). Anchored at UTC midnight so date arithmetic never
 * drifts across a DST boundary — mixed `T00:00:00` / `T00:00:00Z` parsing was a reference bug.
 */
export function parseBusinessDate(value: BusinessDate): Date {
  if (!isBusinessDate(value)) {
    throw new Error(`Invalid business date: ${value}`);
  }
  const { year, month, day } = partsOf(value);
  return new Date(Date.UTC(year, month - 1, day));
}

export function formatBusinessDate(date: Date): BusinessDate {
  return date.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to`; negative when `to` precedes `from`. */
export function daysBetween(from: BusinessDate, to: BusinessDate): number {
  return (parseBusinessDate(to).getTime() - parseBusinessDate(from).getTime()) / MS_PER_DAY;
}

export function addDays(date: BusinessDate, days: number): BusinessDate {
  return formatBusinessDate(new Date(parseBusinessDate(date).getTime() + days * MS_PER_DAY));
}

/**
 * D4 flag: entry older than 7 Berlin days. Still set on rows for reporting/audit.
 * The change-reason dialog uses the anySaved week rule (not this flag); see saveWeek.
 */
export function isRetroactive(
  entryDate: BusinessDate,
  today: BusinessDate = berlinToday(),
): boolean {
  return daysBetween(entryDate, today) > 7;
}

/** ISO weekday, Monday = 1 … Sunday = 7. */
export function isoWeekday(date: BusinessDate): number {
  return parseBusinessDate(date).getUTCDay() || 7;
}

/**
 * The Thursday of an ISO week — the day that decides which month (and year) the week
 * belongs to. Used for week notes and the capacity report (PLAN.md D-07, PRD §6.5).
 * January 4th is by definition in ISO week 1.
 */
export function isoWeekThursday(year: number, isoWeek: number): BusinessDate {
  const jan4 = `${String(year).padStart(4, "0")}-01-04`;
  return addDays(jan4, 4 - isoWeekday(jan4) + (isoWeek - 1) * 7);
}

/** The ISO week-year and week number a date falls in. */
export function isoWeekOf(date: BusinessDate): { year: number; week: number } {
  const thursday = addDays(date, 4 - isoWeekday(date));
  const year = Number(thursday.slice(0, 4));
  const week = Math.round(daysBetween(isoWeekThursday(year, 1), thursday) / 7) + 1;
  return { year, week };
}

/** Calendar month a date belongs to, as the 1-based pair the lock tables use. */
export function monthOf(date: BusinessDate): { year: number; month: number } {
  return { year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) };
}

/**
 * Latest Project Entry Date the assign/edit/import UI and API may accept:
 * last calendar day of the Berlin month that is two months after `today`
 * (e.g. 2026-08-12 → 2026-10-31). Cap further with `min(project.endDate, horizon)`.
 */
export function entryDateMaxHorizon(today: BusinessDate = berlinToday()): BusinessDate {
  const { year, month } = partsOf(today);
  const shifted = year * 12 + (month - 1) + 2;
  const horizonYear = Math.floor(shifted / 12);
  const horizonMonthIndex = shifted % 12; // 0-based
  // Day 0 of the following month = last day of the horizon month.
  return formatBusinessDate(new Date(Date.UTC(horizonYear, horizonMonthIndex + 1, 0)));
}
